import React, { useEffect, useState } from "react";
import { sourceLabel } from "@/lib/api";

export function SourcePill({ source }) {
  return (
    <span className="caa-source-pill" data-testid={`source-pill-${source}`}>
      {sourceLabel(source)}
    </span>
  );
}

export function StatusTag({ status }) {
  const cls =
    {
      open: "caa-status caa-status-live",
      sold: "caa-status caa-status-sold",
      passed: "caa-status caa-status-passed",
      upcoming: "caa-status caa-status-upcoming",
      closed: "caa-status caa-status-closed",
    }[status] || "caa-status caa-status-closed";
  const label =
    { open: "Live", sold: "Sold", passed: "Passed in", upcoming: "Upcoming", closed: "Closed" }[status] ||
    status;
  return (
    <span className={cls} data-testid={`status-tag-${status}`}>
      <span className="dot" />
      {label}
    </span>
  );
}

/** Live-ticking countdown pill for a lot's closing_at (AEST clock reads locally).
 * - Shows "—" when closing_at is null (e.g. RB unscheduled lots).
 * - < 1h to close → red urgent pulse
 * - < 24h → amber
 * - >= 24h → neutral teal
 * - After close: "closed Xd ago" in muted grey. Blank beats a fake future date.
 */
export function ClosingChip({ closingAt, testId }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!closingAt) return;
    const t = new Date(closingAt).getTime();
    // tick every 30s when >1h out, every 5s under 1h
    const interval = Math.abs(t - Date.now()) < 3600000 ? 5000 : 30000;
    const id = setInterval(() => setNow(Date.now()), interval);
    return () => clearInterval(id);
  }, [closingAt]);

  if (!closingAt) {
    return (
      <span className="caa-closing-chip caa-closing-none" data-testid={testId}>
        —
      </span>
    );
  }
  const t = new Date(closingAt).getTime();
  const diff = t - now;
  const isPast = diff < 0;
  const abs = Math.abs(diff);
  const mins = Math.floor(abs / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);

  let label;
  if (days > 0) label = `${days}d ${hours % 24}h`;
  else if (hours > 0) label = `${hours}h ${mins % 60}m`;
  else label = `${Math.max(0, mins)}m`;

  let urgency;
  if (isPast) urgency = "past";
  else if (abs < 3600000) urgency = "urgent";     // < 1h
  else if (abs < 86400000) urgency = "soon";      // < 24h
  else urgency = "later";

  const prefix = isPast ? "closed" : "closes in";
  return (
    <span
      className={`caa-closing-chip caa-closing-${urgency}`}
      data-testid={testId}
      title={new Date(closingAt).toLocaleString("en-AU", { timeZone: "Australia/Brisbane" }) + " AEST"}
    >
      {prefix} {label}{isPast ? " ago" : ""}
    </span>
  );
}

